import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for debouncing values that works with React StrictMode
 * 
 * @param {any} value - The value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {any} The debounced value
 * 
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebounce(searchTerm, 300);
 */
export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const lastUpdateTimeRef = useRef(0);
  const valueRef = useRef(value);
  const timeoutRef = useRef();

  // Update the current value ref
  valueRef.current = value;

  const updateDebouncedValue = useCallback(() => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

    // Only update if enough time has passed since the last update
    if (timeSinceLastUpdate >= delay) {
      lastUpdateTimeRef.current = now;
      setDebouncedValue(valueRef.current);
    } else {
      // Schedule another check after the remaining time
      const remainingTime = delay - timeSinceLastUpdate;
      timeoutRef.current = setTimeout(updateDebouncedValue, remainingTime);
    }
  }, [delay]);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // For the first value, set it immediately
    if (lastUpdateTimeRef.current === 0) {
      lastUpdateTimeRef.current = Date.now();
      setDebouncedValue(value);
      return;
    }

    // For subsequent values, use our custom debounce logic
    timeoutRef.current = setTimeout(updateDebouncedValue, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay, updateDebouncedValue]);

  return debouncedValue;
}

export default useDebounce;
